import { Fragment, useEffect, useState } from "react";
import moment from "moment";

import { InvoiceService } from "../../hoa-don/InvoiceService";
import { RegisterInvoiceService } from "../../../service/invoice/RegisterInvoiceService";
import { CurrencyService } from "../../danh-muc/tien-te/CurrencyService";

import { useToolbar } from "../../../shared/hooks/Toolbar";
import { useDataTable } from "../../../shared/hooks/DataTable";
import { useSign } from "../../../shared/component/Sign";

import { DialogCrup } from "../../hoa-don/component/DialogCrup";
import { DialogView } from "../../hoa-don/component/DialogView";
import { DialogSendEmail } from "../../hoa-don/component/DialogSendEmail";

import { DialogStatement } from "./DialogStatement";
import { DialogAttachment } from "./DialogAttachment";
import DialogBangKe from "./DialogBangKe";
import { StatementInvoiceService } from "../StatementInvoiceService";
import { DialogCreateMultipleInvoiceOptions } from "./DialogCreateMultipleInvoiceOptions";
import { useTranslation } from "../../../shared/language";

type MultipleInvoiceMode = "substitute" | "adjustment";

const modeConfig = {
  substitute: {
    mode: "substitute",
    invoiceStatus: 3,
    relatedInvoiceProperty: 3,
    invoiceType: 1,
    documentType: InvoiceService.documentType_Substitute,
    getList: (params) => InvoiceService.getListSubstitutes(params),
  },
  adjustment: {
    mode: "adjustment",
    invoiceStatus: 2,
    relatedInvoiceProperty: 4,
    invoiceType: 2,
    documentType: InvoiceService.documentType_Adjust,
    getList: (params) => InvoiceService.getListAdjustments(params),
  },
};

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "");

const getOriginalInvoiceId = (item) =>
  item?.originalInvoiceId || item?.invoiceId || item?.relatedInvoiceId;

const buildSelectedInvoicesFromInvoiceList = (invoiceList) => {
  const invoiceIds = Array.from(
    new Set(
      (invoiceList?.invoiceListItems || [])
        .map(getOriginalInvoiceId)
        .filter(Boolean),
    ),
  );

  return invoiceIds.map((id) => ({ id }));
};

export const MultipleInvoiceList = ({ mode }: { mode: MultipleInvoiceMode }) => {
  const { t } = useTranslation();
  const baseConfig = modeConfig[mode];
  const config = {
    ...baseConfig,
    mode,
    header: t(mode === "substitute" ? "common.multipleInvoiceReplacement" : "common.multipleInvoiceAdjustment"),
    createdGroupHeader: t(mode === "substitute" ? "common.replacement" : "common.adjustment"),
    actionLabel: t(mode === "substitute" ? "common.createMultipleReplacementInvoice" : "common.createMultipleAdjustmentInvoice"),
    actionName: t(mode === "substitute" ? "common.replacement" : "common.adjustment"),
    title: t(mode === "substitute" ? "common.replacement" : "common.adjustment").toLowerCase(),
  };
  const dataKey = "invoiceId";

  const [displayCreateOptions, setDisplayCreateOptions] = useState(false);
  const [displayBangKe, setDisplayBangKe] = useState(false);
  const [displayCrup, setDisplayCrup] = useState(false);
  const [displayStatement, setDisplayStatement] = useState(false);
  const [displayUpload, setDisplayUpload] = useState(false);
  const [displayView, setDisplayView] = useState(false);
  const [displaySendEmail, setDisplaySendEmail] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceList, setInvoiceList] = useState(null);
  const [registerInvoice, setRegisterInvoice] = useState(null);
  const [registerInvoices, setRegisterInvoices] = useState([]);
  const [currencies, setCurrencies] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState(null);
  const [statementData, setStatementData] = useState(null);

  useEffect(() => {
    RegisterInvoiceService.getUsingList({
      maxResultCount: 1000,
      Sorting: "creationTime",
      SortType: "DESCEND",
    }).then(({ items }) => {
      const nextItems = (items || []).map((item) => ({
        ...item,
        label: `${item.symbolCode} - ${item.invoiceTypeName}`,
      }));
      setRegisterInvoices(nextItems);
      setRegisterInvoice(nextItems[0] || null);
    });

    CurrencyService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      setCurrencies(items);
    });

    InvoiceService.getPaymentTypes().then(({ items }) => {
      setPaymentTypes(
        (items || []).map((item) => ({ value: item.name, label: item.name })),
      );
    });
  }, []);

  const viewInvoice = (id) => {
    setSelectedInvoice({ id });
    setDisplayView(true);
  };

  const print = (invoice) => {
    StatementInvoiceService.getByInvoice(invoice.invoiceId || invoice.id).then(
      (res) => {
        window.open(StatementInvoiceService.getPdfUrl(res.id), "_blank");
      },
    );
  };

  const {
    render: renderDataTable,
    reloadLazyData,
    selectedItems,
  } = useDataTable({
    header: config.header,
    dataKey,
    filterDelay: 500,
    columns: [
      {
        field: "invoiceSerial",
        header: t("common.symbol"),
        width: 100,
        matchMode: "contains",
      },
      {
        field: "invoiceNumber",
        header: t("common.invoiceNo"),
        width: 110,
        matchMode: "contains",
        dataType: "custom",
        filterType: "number",
        customCell(rowData): any {
          return (
            <span
              className="underline text-primary"
              onClick={() => viewInvoice(rowData.invoiceId || rowData.id)}
            >
              {rowData.invoiceNumber}
            </span>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "invoiceDate",
        header: t("common.invoiceDate"),
        width: 120,
        matchMode: "equals",
        filterType: "range-date",
        filterField: "FromDate@ToDate",
        dataType: "date",
      },
      {
        field: "sendTaxStatus",
        header: t("common.taxAuthoritySendStatus"),
        width: 180,
        matchMode: "equals",
        filterType: "dropdown",
        filterOptions: InvoiceService.sendTaxStatuses,
        dataType: "custom",
        customCell(rowData: any): any {
          return (
            <span
              className={`${[3, 4, 5].includes(rowData.sendTaxStatus) ? "underline " : ""}send-tax-status send-tax-status-${rowData.sendTaxStatus}`}
            >
              {InvoiceService.getSendTaxStatusLabel(rowData.sendTaxStatus)}
            </span>
          );
        },
      },
      {
        field: "buyerLegalName",
        header: t("catalog.customer"),
        minWidth: 200,
        matchMode: "contains",
        dataType: "custom",
        customCell(rowData: any): any {
          return rowData.buyerLegalName || rowData.buyerDisplayName;
        },
      },
      {
        field: "documentNoView",
        header: t("common.statement"),
        width: 110,
        sortable: false,
        dataType: "custom",
        customCell(rowData): any {
          const text = rowData.hasDocument
            ? rowData.documentNo
            : t("common.statementCreate");
          const className = rowData.hasDocument
            ? "underline send-tax-status send-tax-status-4"
            : "underline text-primary";
          return (
            <span
              className={className}
              onClick={() => setDisplayStatement(true)}
            >
              {text}
            </span>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "rowActions",
        header: t("common.actions"),
        width: 100,
        sortable: false,
        dataType: "custom",
        customCell(rowData: any): any {
          return (
            <div className="flex gap-3">
              <i
                className="pi pi-link"
                title={t("common.attachment")}
                onClick={() => setDisplayUpload(true)}
              ></i>
              {rowData.hasDocument && (
                <i
                  className="pi pi-print"
                  title={t("common.printStatement")}
                  onClick={() => print(rowData)}
                ></i>
              )}
              <i
                className="pi pi-envelope"
                title={t("common.emailStatementForSignature")}
                onClick={() => {
                  setDisplaySendEmail(true);
                  StatementInvoiceService.getByInvoice(
                    rowData.invoiceId || rowData.id,
                  ).then((res) => {
                    setStatementData(res);
                  });
                }}
              ></i>
            </div>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "relatedInvoiceListNumber",
        header: t("common.invoiceListNo"),
        width: 140,
        matchMode: "contains",
        dataType: "custom",
        customCell(rowData): any {
          return rowData.relatedInvoiceListNumber || rowData.invoiceListNumber;
        },
      },
      {
        field: "relatedInvoiceListDate",
        header: t("common.invoiceListDate"),
        width: 130,
        matchMode: "equals",
        filterType: "range-date",
        filterField: "relatedInvoiceListDate@relatedInvoiceListDate",
        dataType: "custom",
        customCell(rowData): any {
          return formatDate(
            rowData.relatedInvoiceListDate || rowData.invoiceListDate,
          );
        },
      },
      {
        field: "invoiceNote",
        header: t("common.errorReason"),
        minWidth: 180,
        matchMode: "contains",
      },
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      if (!params.sortType) {
        params.sortType = "DESCEND";
      }
      if (!params.sorting) {
        params.sorting = "invoiceDate";
      }
      params.RelatedInvoiceProperty = config.relatedInvoiceProperty;
      return config.getList(params);
    },
    selectionMode: "single",
  });

  const { renderToolbar } = useToolbar({
    dataKey,
    reloadLazyData,
  });

  const { renderSign, sign } = useSign({ onComplete: reloadLazyData });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {renderSign()}

      <DialogCreateMultipleInvoiceOptions
        display={displayCreateOptions}
        setDisplay={setDisplayCreateOptions}
        config={config}
        onCreateInvoice={() => {
          setSelectedInvoices([]);
          setInvoiceList(null);
          setDisplayCrup(true);
        }}
        onCreateInvoiceList={() => {
          setSelectedInvoices([]);
          setInvoiceList(null);
          setDisplayBangKe(true);
        }}
      />

      <DialogBangKe
        display={displayBangKe}
        setDisplay={setDisplayBangKe}
        selectedInvoices={selectedInvoices}
        invoiceType={config.invoiceType}
        registerInvoices={registerInvoices}
        onSubmit={(result) => {
          setInvoiceList(result);
          setSelectedInvoices(buildSelectedInvoicesFromInvoiceList(result));
          setDisplayBangKe(false);
          setDisplayCrup(true);
        }}
      />

      <DialogCrup
        display={displayCrup}
        setDisplay={setDisplayCrup}
        reloadLazyData={reloadLazyData}
        registerInvoices={registerInvoices}
        currencies={currencies}
        paymentTypes={paymentTypes}
        registerInvoice={registerInvoice}
        selectedItem={null}
        selectedInvoices={selectedInvoices}
        invoiceList={invoiceList}
        relatedInvoiceProperty={config.relatedInvoiceProperty}
        invoiceStatus={config.invoiceStatus}
        sign={sign}
      />

      <DialogAttachment
        display={displayUpload}
        setDisplay={setDisplayUpload}
        selectedItem={selectedItems}
      />

      <DialogStatement
        display={displayStatement}
        setDisplay={setDisplayStatement}
        selectedItem={selectedItems}
        documentType={config.documentType}
        reloadLazyData={reloadLazyData}
        title={config.title}
      />

      <DialogView
        display={displayView}
        setDisplay={setDisplayView}
        selectedItem={selectedInvoice}
      />

      <DialogSendEmail
        display={displaySendEmail}
        setDisplay={setDisplaySendEmail}
        sendType={8}
        selectedItems={statementData}
      />
    </Fragment>
  );
};
